import React from 'react';
import { RocketLaunchIcon, DocumentTextIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const TransparencyCharterStage1 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection 
        title="Stage 1: Review Feedback & Data" 
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />} 
        heading
      >
        <div className="bg-gray-800/30 p-6 rounded-lg border border-gray-700/50">
          <h3 className="text-xl font-semibold text-white mb-4">TL;DR</h3>
          
          <ExpandableCallout 
            type="info" 
            title="What do I need to DO in this stage?"
            defaultOpen={true}
          >
            <p className="text-gray-300 mb-4">You need to do 3 x things:</p>
            <ol className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1</span>
                <span className="text-gray-300">Identify where lack of clarity & communication is causing negative sentiment</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2</span>
                <span className="text-gray-300">Connect dots to relevant Data & Metrics which are being negatively affected</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3</span>
                <span className="text-gray-300">Build a case to create more clarity & alignment via a Transparency Charter</span>
              </li>
            </ol>
            <p className="text-gray-300 mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </div>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-primary-light mr-3" />
            Action: Review Feedback & Data
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Sporadic, anecdotal data (A.K.A 'Anecdata') is likely to fall on deaf ears with most exec teams. This is a great opportunity to take a moment to pull together as much as you can, and tie what you do know to business performance as best as you can.
            </p>
            
            <p className="text-gray-300 mt-4">You want to understand:</p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">What are people telling you? (e.g. where is there a lack of clarity, or transparency)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">What is it tied to? (e.g. Trigger event - M&A? Leadership Change?, Layoffs?)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">How is it negatively affecting business performance (Data, Metrics)</span>
              </li>
            </ul>
            
            <p className="text-gray-300 mt-4">There are 2 goals here:</p>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Show Exec exactly where & why lack of clarity & communication is causing problems</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Prove those problems are negatively impacting business performance</span>
              </li>
            </ul>
          </div>
          
          <ExpandableCallout 
            type="tip" 
            title="Advice: Utilise as much as you can…"
            className="mt-6"
          >
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Exit Interview data</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Engagement Survey Data</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Ad-hoc feedback</span>
              </li>
            </ul>
          </ExpandableCallout>
        </PlaybookCard>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Transparency Charter Buy-In
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Alongside presenting the core data and argument around where things are going wrong, and how they are affecting the business, you will want to introduce the idea of utilising a Transparency Charter as the remedy. The rest of this playbook will walk you through 'how' to build that, but before you can, you will need buy-in. If helpful, you can take some inspiration from the following slide deck as a template.
            </p>
            
            <div className="mt-6">
              <ToolCallout
                tool="pdf"
                label="Transparency Charter Template Slides"
                href="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/Transparency_Charter.pdf"
                description="Slide deck template to help you present the case for a transparency charter"
              />
            </div>
          </div>
        </PlaybookCard>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <RocketLaunchIcon className="h-6 w-6 text-primary-light mr-3" />
            Target Outcome
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Your aim at this stage when presenting something should be to gain buy-in at Exec level to co-create a transparency charter, which is covered in the stages that follow. Right now you are just establishing & presenting the need for something documented here that provides employees with more clarity around what is and is not shared with them.
            </p>
            
            <div className="mt-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <p className="text-gray-300">
                After completing this stage, proceed to <Link to="/playbooks/transparency-charter/stage-2" className="text-primary hover:text-primary-light">Stage 2: Transparency Charter</Link> where you'll design and document your charter.
              </p>
            </div>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default TransparencyCharterStage1; 